/**
 * Вкладки застосунку одним списком: верхня панель показує їх рядком, меню на
 * телефоні — стовпчиком. Два місця, один порядок і одні права.
 */

import { useCan, useData } from '../store/data.js';

export interface NavItem {
  to: string;
  label: string;
  /** Кореневий шлях збігається з усім — його звіряємо повністю. */
  end?: boolean;
  title?: string;
  /**
   * Підпис для телефона. Повні назви розділів у сегментоване кермо не влазять,
   * а коротких вистачає: поруч стоять лише чотири вкладки, і сплутати їх нема з чим.
   */
  short?: string;
  /**
   * Розділ, у який заходять не щодня. На телефоні такі не займають місця в
   * кермі — вони живуть у меню й з'являються в кермі, лише коли ти в них.
   */
  aside?: boolean;
  /**
   * Розділ, у якого на телефоні власна кнопка-значок у шапці, а не місце в
   * кермі. Такий один — «Налаштування курсу»: заходять у нього з будь-якої
   * сторінки, а повний підпис розпер би кермо за край екрана.
   */
  icon?: 'gear';
}

/** Сталі вкладки телефона; інші розділи живуть у меню. */
export const isMobileTab = (item: NavItem): boolean =>
  ['/', '/duty', '/away', '/leave'].includes(item.to);

/** Порядок вкладок: напрямок «робочих столів» рахуємо за ним. */
const NAV_ORDER = ['', 'duty', 'away', 'leave', 'people', 'reports', 'settings', 'program'];

export function navSection(pathname: string): string {
  return pathname.split('/').filter(Boolean)[0] ?? '';
}

function navIndex(section: string): number {
  const i = NAV_ORDER.indexOf(section);
  return i < 0 ? 0 : i;
}

let navClearTimer = 0;

/**
 * Позначити напрямок для анімації як у Spaces macOS — до старту View Transition.
 * Атрибут знімається після тривалості анімації.
 */
export function markSpaceNav(fromPath: string, toPath: string): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.removeAttribute('data-nav');
    return;
  }
  const from = navIndex(navSection(fromPath));
  const to = navIndex(navSection(toPath));
  const root = document.documentElement;
  if (to === from) {
    root.removeAttribute('data-nav');
    return;
  }
  root.dataset.nav = to > from ? 'forward' : 'back';
  window.clearTimeout(navClearTimer);
  navClearTimer = window.setTimeout(() => root.removeAttribute('data-nav'), 700);
}

export function useNavItems(): NavItem[] {
  const session = useData(s => s.session);
  const canSettings = useCan('settings.view');
  const canPeople = useCan('people.view');
  const canDocs = useCan('documents.generate');
  const reportsOn = session?.org.settings.features.reports ?? false;

  const items: NavItem[] = [
    // Таблиці особового складу — з «о/с» у назві. На телефоні смуга вузька,
    // тож там лишаються короткі підписи без нього.
    { to: '/', label: 'Харчування о/с', short: 'Харчування', end: true },
    { to: '/duty', label: 'Наряди о/с', short: 'Наряди' },
    { to: '/away', label: 'Статус о/с', short: 'Статус', title: 'Статус особового складу: відпустки, ЛР' },
    { to: '/leave', label: 'Звільнення о/с', short: 'Звільнення', title: 'Рапорти на звільнення' },
    ...(canPeople ? [{ to: '/people', label: 'Особовий склад', short: 'о/с' }] : []),
    ...(canDocs && reportsOn
      ? [{ to: '/reports', label: 'Рапорти', title: 'Рапорти в один клік' }]
      : []),
    ...(canSettings
      ? [
          {
            to: '/settings',
            label: 'Налаштування',
            icon: 'gear' as const,
            aside: true,
            title: `Налаштування курсу ${session?.org.shortName ?? ''}`,
          },
        ]
      : []),
    { to: '/program', label: 'Про програму', short: 'Програма', aside: true, title: 'Про програму' },
  ];
  return items;
}
